import React, { MouseEvent, ReactNode } from "react";
import { isValid, parseISO, compareDesc } from "date-fns";
import MuiTableBody from "@material-ui/core/TableBody";

import {
  DetailCardField,
  DetailCardTitleProperty,
  SortTableBy,
  TableData,
  TableColumn,
  TableOrder,
  DetailCardAction
} from "./types";
import TableDetailCard from "./TableDetailCard";
import TableRow from "./TableRow";

type Props = {
  columns: TableColumn[];
  data: TableData[];
  detailCardActions?: DetailCardAction[];
  detailCardTitle?: DetailCardTitleProperty;
  checkSelected: (id: string) => boolean;
  onSelectRow: (id: string) => void;
  onRowClickGetDetailData?: (row: any) => Promise<void>;
  order: TableOrder;
  orderBy: SortTableBy;
  page: number;
  renderDetailCard?: (data: DetailCardField[]) => ReactNode;
  rowsPerPage: number;
  selectable: boolean;
};

type State = {
  detailData: any;
  detailCardPosition: { left: number; top: number };
  detailCardAnchor: EventTarget | HTMLElement | null;
};

class TableBody extends React.Component<Props, State> {
  desc: (a: any, b: any, orderBy: SortTableBy) => number;
  getSorting: (
    order: TableOrder,
    orderBy: SortTableBy
  ) => (a: any, b: any) => number;
  isValueDate: (value: string) => boolean;
  stableSort: (array: any[], cmp: (a: any, b: any) => number) => any[];

  constructor(props: Props) {
    super(props);
    this.state = {
      detailData: null,
      detailCardPosition: { left: 0, top: 0 },
      detailCardAnchor: null
    };
    this.openDetailCard = this.openDetailCard.bind(this);
    this.closeDetailCard = this.closeDetailCard.bind(this);

    this.isValueDate = (value: string | Date | number) => {
      const date = typeof value === "string" ? parseISO(value) : value;
      return isValid(date);
    };

    this.desc = (a: any, b: any, orderBy: SortTableBy) => {
      const x = orderBy.parentId
        ? a[orderBy.parentId][orderBy.id]
        : a[orderBy.id];
      const y = orderBy.parentId
        ? b[orderBy.parentId][orderBy.id]
        : b[orderBy.id];
      if (this.isValueDate(x) && this.isValueDate(y)) {
        return compareDesc(new Date(x), new Date(y));
      }
      if (y < x) {
        return -1;
      }
      if (y > x) {
        return 1;
      }
      return 0;
    };

    this.getSorting = (order: TableOrder, orderBy: SortTableBy) => {
      return order === TableOrder.Desc
        ? (a, b) => this.desc(a, b, orderBy)
        : (a, b) => -this.desc(a, b, orderBy);
    };

    this.stableSort = (array: any[], cmp: (a: any, b: any) => number) => {
      const stabilizedThis = array.map((el, index) => [el, index]);
      stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
      });
      return stabilizedThis.map(el => el[0]);
    };
  }

  async openDetailCard(e: MouseEvent<HTMLElement>, rowId: string) {
    e.preventDefault();
    e.persist();

    if (this.props.onRowClickGetDetailData) {
      const data = await this.props.onRowClickGetDetailData(rowId);

      this.setState({
        detailData: data,
        detailCardPosition: { left: e.clientX, top: e.clientY }
      });
    } else {
      if (!rowId) return;
      const data = this.props.data.find((row: TableData) => {
        return row.id === rowId;
      });
      this.setState({
        detailData: data,
        detailCardPosition: { left: e.clientX, top: e.clientY }
      });
    }
  }

  closeDetailCard() {
    this.setState({
      detailData: null
    });
  }

  render() {
    const {
      data,
      detailCardActions,
      detailCardTitle,
      columns,
      order,
      orderBy,
      selectable,
      checkSelected,
      rowsPerPage,
      page,
      onSelectRow
    } = this.props;

    return (
      <MuiTableBody>
        {this.stableSort(data, this.getSorting(order, orderBy))
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((n: TableData) => {
            const isSelected = checkSelected(n.id);
            return (
              <TableRow
                key={n.id}
                id={n.id}
                data={n}
                selectable={selectable}
                columns={columns.filter(c => !c.hide)}
                isSelected={isSelected}
                onSelectRow={onSelectRow}
                onOpenDetail={this.openDetailCard}
              />
            );
          })}

        {this.state.detailData && (
          <TableDetailCard
            actions={detailCardActions}
            anchor={this.state.detailCardPosition}
            columns={columns}
            data={this.state.detailData}
            open
            onClose={this.closeDetailCard}
            titleProperty={detailCardTitle}
          />
        )}
      </MuiTableBody>
    );
  }
}

export default TableBody;
